import { useEffect, useState } from 'react'
import { notifications } from '@mantine/notifications'
import {
  useAplicarPlanosPadrao,
  useCatalogoPlanosPadrao,
} from '../../hooks/usePmoc'
import type { PMOC } from '../../types'

interface Props {
  open: boolean
  onClose: () => void
  pmoc: PMOC
}

const TIPO_LABEL: Record<string, string> = {
  split_hi_wall: 'Split Hi-Wall',
  split_cassete: 'Split Cassete',
  split_piso_teto: 'Split Piso-Teto',
  split_duto: 'Split Dutado',
  vrf_condensadora: 'VRF Condensadora',
  vrf_evaporadora: 'VRF Evaporadora',
  chiller: 'Chiller',
  self_contained: 'Self-Contained',
  uta: 'UTA',
  fancoil: 'Fan Coil',
  torre_resfriamento: 'Torre de Resfriamento',
  condensadora_ar: 'Condensadora a Ar',
  janela: 'Ar de Janela',
  ptac: 'PTAC',
  _geral: '🌐 Genérico (QAI semestral, etc)',
}

export default function AplicarPlanosNbrModal({ open, onClose, pmoc }: Props) {
  const catalogo = useCatalogoPlanosPadrao()
  const aplicar = useAplicarPlanosPadrao(pmoc.id)
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set())
  const [incluirGeral, setIncluirGeral] = useState(true)

  // Pré-seleciona tipos detectados nos equipamentos do PMOC
  useEffect(() => {
    if (!open) return
    const tipos = new Set<string>()
    pmoc.equipamentos?.forEach((eq) => {
      if (eq.tipo) tipos.add(eq.tipo)
    })
    setSelecionados(tipos)
  }, [open, pmoc.equipamentos])

  if (!open) return null

  function toggle(tipo: string) {
    setSelecionados((prev) => {
      const next = new Set(prev)
      if (next.has(tipo)) next.delete(tipo)
      else next.add(tipo)
      return next
    })
  }

  async function handleAplicar() {
    if (selecionados.size === 0 && !incluirGeral) {
      notifications.show({ color: 'orange', message: 'Selecione ao menos um tipo.' })
      return
    }
    try {
      const r = await aplicar.mutateAsync({
        tipos_equipamento: Array.from(selecionados),
        incluir_geral: incluirGeral,
      })
      notifications.show({
        color: 'green',
        message: `${r.total_criados} plano(s) NBR adicionado(s) ao PMOC.`,
      })
      onClose()
    } catch (err) {
      const msg =
        (err as { response?: { data?: { detail?: string; status?: string } } })?.response?.data
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: msg?.detail ?? msg?.status ?? 'Erro ao aplicar planos.',
      })
    }
  }

  const planosPorTipo = catalogo.data?.planos_por_tipo ?? {}
  const tiposNoCatalogo = Object.keys(planosPorTipo).filter((t) => t !== '_geral')

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: 8,
          maxWidth: 720,
          width: '90%',
          maxHeight: '85vh',
          overflow: 'auto',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1.1rem' }}>
          Aplicar planos padrão de manutenção — NBR 13971:2014
        </h2>
        <p style={{ color: '#666', fontSize: '0.85rem', margin: '0.25rem 0 1rem' }}>
          Selecione os tipos de equipamento do PMOC. Para cada tipo, o sistema cria
          automaticamente os planos preventivos e de higienização por componentes.
          A qualidade do ar permanece separada, conforme NBR 17037:2023. A operação
          é idempotente: planos existentes não são duplicados.
        </p>

        {catalogo.isLoading ? (
          <p>Carregando catálogo...</p>
        ) : (
          <>
            <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem' }}>
              {tiposNoCatalogo.map((tipo) => {
                const planos = planosPorTipo[tipo]
                const checked = selecionados.has(tipo)
                return (
                  <label
                    key={tipo}
                    style={{
                      display: 'flex',
                      gap: '0.5rem',
                      padding: '0.6rem',
                      border: `2px solid ${checked ? '#1565C0' : '#E0E0E0'}`,
                      borderRadius: 4,
                      cursor: 'pointer',
                      background: checked ? '#E3F2FD' : 'white',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(tipo)}
                      data-testid={`nbr-tipo-${tipo}`}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{TIPO_LABEL[tipo] ?? tipo}</div>
                      <div style={{ fontSize: '0.8rem', color: '#666', marginTop: 4 }}>
                        {planos.length} planos:{' '}
                        {planos
                          .map((p) => `${p.periodicidade} (${p.tipo_os})`)
                          .join(', ')}
                      </div>
                    </div>
                  </label>
                )
              })}
            </div>

            <label
              style={{
                display: 'flex',
                gap: '0.5rem',
                padding: '0.6rem',
                background: '#FFF8E1',
                borderRadius: 4,
                marginBottom: '1rem',
              }}
            >
              <input
                type="checkbox"
                checked={incluirGeral}
                onChange={(e) => setIncluirGeral(e.target.checked)}
                data-testid="nbr-incluir-geral"
              />
              <div>
                <strong>{TIPO_LABEL['_geral']}</strong>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                  {planosPorTipo._geral?.map((p) => p.descricao).join(' · ')}
                </div>
              </div>
            </label>
          </>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={aplicar.isPending}
            style={{
              padding: '0.5rem 1rem',
              background: 'white',
              color: '#666',
              border: '1px solid #888',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleAplicar}
            disabled={aplicar.isPending}
            data-testid="nbr-aplicar-submit"
            style={{
              padding: '0.5rem 1rem',
              background: '#2E7D32',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {aplicar.isPending ? 'Aplicando...' : 'Aplicar planos selecionados'}
          </button>
        </div>
      </div>
    </div>
  )
}
